import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ProductCard } from '../components/products/ProductCard';
import { productsAPI } from '../lib/api';
import { useAnalytics } from '../context/AnalyticsContext';
import { usePageMeta } from '../lib/usePageMeta';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, SlidersHorizontal, X, Cpu, Monitor, CircuitBoard, MemoryStick, HardDrive, Zap, Box, Fan, ChevronRight, ArrowUpDown } from 'lucide-react';
import { formatPrice, cn, getStockStatus } from '../lib/utils';
import { productUrl } from '../lib/urlHelpers';

var BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
function proxyImg(url) {
    if (!url || url.startsWith('data:') || url.startsWith(BACKEND_URL)) return url;
    return BACKEND_URL + '/api/images/proxy?url=' + encodeURIComponent(url);
}

var PC_CATEGORIES = new Set(['gaming_pc', 'office_pc', 'workstation']);

var specLabels = { cpu: 'CPU', gpu: 'GPU', ram: 'RAM', storage: 'Tárhely', psu: 'Tápegység' };
var specIcons = { cpu: Cpu, gpu: Monitor, ram: MemoryStick, storage: HardDrive, psu: Zap };

const componentIcons = { cpu: Cpu, gpu: Monitor, motherboard: CircuitBoard, ram: MemoryStick, storage: HardDrive, psu: Zap, case: Box, cooling: Fan };

const categoryNames = {
    gaming_pc: 'Gamer PC-k',
    office_pc: 'Irodai PC-k',
    workstation: 'Munkaállomások',
    laptop: 'Laptopok',
    component: 'Alkatrészek',
    cpu: 'Processzorok',
    gpu: 'Videokártyák',
    motherboard: 'Alaplapok',
    ram: 'Memória',
    storage: 'Tárhely',
    psu: 'Tápegységek',
    case: 'Számítógépházak',
    cooling: 'Hűtés',
    mobile_phone: 'Mobiltelefonok',
    accessory: 'Tartozékok',
    monitor: 'Monitorok',
    keyboard: 'Billentyűzetek',
    mouse: 'Egerek',
    cable: 'Kábelek',
    headset: 'Fejhallgatók'
};

const getCategoryName = (cat) => categoryNames[cat] || cat;

// Category-specific filter configurations
const categoryFilters = {
    // PC categories (gaming_pc, office_pc, workstation)
    pc: {
        title: 'PC Szűrők',
        filters: [
            { key: 'cpu', label: 'Processzor', type: 'select', options: ['Intel i9', 'Intel i7', 'Intel i5', 'AMD Ryzen 9', 'AMD Ryzen 7', 'AMD Ryzen 5', 'AMD Threadripper', 'Intel Xeon'] },
            { key: 'gpu', label: 'Videokártya', type: 'select', options: ['RTX 4090', 'RTX 4080', 'RTX 4070', 'RTX 4060', 'RTX 3070', 'RX 7900', 'RX 7800', 'Intel UHD'] },
            { key: 'ram', label: 'Memória', type: 'select', options: ['16GB', '32GB', '64GB', '128GB'] },
        ]
    },
    // Laptop category
    laptop: {
        title: 'Laptop Szűrők',
        filters: [
            { key: 'brand', label: 'Márka', type: 'select', options: ['Lenovo', 'ASUS', 'HP', 'Dell', 'Acer', 'MSI', 'Apple'] },
            { key: 'cpu', label: 'Processzor', type: 'select', options: ['Intel Core Ultra 9', 'Intel Core Ultra 7', 'Intel Core Ultra 5', 'Intel i9', 'Intel i7', 'Intel i5', 'AMD Ryzen 9', 'AMD Ryzen 7', 'AMD Ryzen 5', 'Apple M4 Pro', 'Apple M4'] },
            { key: 'gpu', label: 'Videokártya', type: 'select', options: ['RTX 4090', 'RTX 4080', 'RTX 4070', 'RTX 4060', 'RTX 3060', 'RX 7700S', 'Intel Iris Xe', 'Integrált'] },
            { key: 'ram', label: 'Memória', type: 'select', options: ['8GB', '16GB', '32GB', '64GB'] },
            { key: 'screen', label: 'Kijelző', type: 'select', options: ['13"', '14"', '15.6"', '16"', '17.3"'] },
        ]
    },
    // Component categories
    cpu: {
        title: 'Processzor Szűrők',
        filters: [
            { key: 'brand', label: 'Márka', type: 'select', options: ['Intel', 'AMD'] },
            { key: 'socket', label: 'Foglalat', type: 'select', options: ['LGA1700', 'LGA1200', 'AM5', 'AM4'] },
            { key: 'cores', label: 'Magok száma', type: 'select', options: ['4', '6', '8', '12', '16', '24'] },
        ]
    },
    gpu: {
        title: 'Videokártya Szűrők',
        filters: [
            { key: 'chipset', label: 'Chipset', type: 'select', options: ['NVIDIA', 'AMD', 'Intel'] },
            { key: 'manufacturer', label: 'Gyártó', type: 'select', options: ['ASUS', 'MSI', 'Gigabyte', 'SAPPHIRE', 'XFX', 'ASRock', 'PowerColor', 'PNY', 'ZOTAC', 'Gainward', 'Palit'] },
            { key: 'vram', label: 'VRAM', type: 'select', options: ['8GB', '12GB', '16GB', '24GB'] },
        ]
    },
    motherboard: {
        title: 'Alaplap Szűrők',
        filters: [
            { key: 'brand', label: 'Márka', type: 'select', options: ['ASUS', 'MSI', 'Gigabyte', 'ASRock'] },
            { key: 'socket', label: 'Foglalat', type: 'select', options: ['LGA1700', 'LGA1200', 'AM5', 'AM4'] },
            { key: 'form_factor', label: 'Méret', type: 'select', options: ['ATX', 'Micro-ATX', 'Mini-ITX'] },
        ]
    },
    ram: {
        title: 'Memória Szűrők',
        filters: [
            { key: 'brand', label: 'Márka', type: 'select', options: ['G.Skill', 'Corsair', 'Kingston', 'Crucial'] },
            { key: 'type', label: 'Típus', type: 'select', options: ['DDR5', 'DDR4'] },
            { key: 'capacity', label: 'Kapacitás', type: 'select', options: ['16GB', '32GB', '64GB'] },
        ]
    },
    storage: {
        title: 'Tárhely Szűrők',
        filters: [
            { key: 'brand', label: 'Márka', type: 'select', options: ['Samsung', 'WD', 'Seagate', 'Crucial'] },
            { key: 'type', label: 'Típus', type: 'select', options: ['NVMe SSD', 'SATA SSD', 'HDD'] },
            { key: 'capacity', label: 'Kapacitás', type: 'select', options: ['256GB', '512GB', '1TB', '2TB', '4TB'] },
        ]
    },
    psu: {
        title: 'Tápegység Szűrők',
        filters: [
            { key: 'brand', label: 'Márka', type: 'select', options: ['Corsair', 'Seasonic', 'EVGA', 'be quiet!'] },
            { key: 'wattage', label: 'Teljesítmény', type: 'select', options: ['550W', '650W', '750W', '850W', '1000W'] },
            { key: 'efficiency', label: 'Hatékonyság', type: 'select', options: ['80+ Bronze', '80+ Gold', '80+ Platinum'] },
        ]
    },
    case: {
        title: 'Ház Szűrők',
        filters: [
            { key: 'brand', label: 'Márka', type: 'select', options: ['NZXT', 'Lian Li', 'Fractal Design', 'Phanteks', 'Corsair'] },
            { key: 'form_factor', label: 'Méret', type: 'select', options: ['Full Tower', 'Mid Tower', 'Mini Tower'] },
        ]
    },
    cooling: {
        title: 'Hűtés Szűrők',
        filters: [
            { key: 'brand', label: 'Márka', type: 'select', options: ['Noctua', 'NZXT', 'Corsair', 'be quiet!', 'Arctic'] },
            { key: 'type', label: 'Típus', type: 'select', options: ['Léghűtés', 'AIO Vízhűtés'] },
        ]
    },
    mobile_phone: {
        title: 'Mobiltelefon Szűrők',
        filters: [
            { key: 'brand', label: 'Márka', type: 'select', options: ['Apple', 'Samsung', 'Xiaomi', 'OnePlus', 'Google', 'Huawei'] },
            { key: 'storage', label: 'Tárhely', type: 'select', options: ['64GB', '128GB', '256GB', '512GB', '1TB'] },
        ]
    },
    accessory: {
        title: 'Tartozék Szűrők',
        filters: [
            { key: 'brand', label: 'Márka', type: 'select', options: ['Logitech', 'Razer', 'SteelSeries', 'HyperX', 'Corsair'] },
            { key: 'type', label: 'Típus', type: 'select', options: ['Monitor', 'Billentyűzet', 'Egér', 'Fejhallgató', 'Kábel', 'Webkamera'] },
        ]
    },
    monitor: {
        title: 'Monitor Szűrők',
        filters: [
            { key: 'brand', label: 'Márka', type: 'select', options: ['Samsung', 'LG', 'ASUS', 'Dell', 'BenQ', 'AOC', 'MSI'] },
            { key: 'size', label: 'Méret', type: 'select', options: ['24"', '27"', '32"', '34"', '49"'] },
            { key: 'panel', label: 'Panel', type: 'select', options: ['IPS', 'VA', 'OLED', 'TN'] },
        ]
    },
    keyboard: {
        title: 'Billentyűzet Szűrők',
        filters: [
            { key: 'brand', label: 'Márka', type: 'select', options: ['Logitech', 'Razer', 'SteelSeries', 'HyperX', 'Corsair', 'Keychron'] },
            { key: 'type', label: 'Típus', type: 'select', options: ['Mechanikus', 'Membrán', 'Optikai'] },
        ]
    },
    mouse: {
        title: 'Egér Szűrők',
        filters: [
            { key: 'brand', label: 'Márka', type: 'select', options: ['Logitech', 'Razer', 'SteelSeries', 'Zowie', 'Corsair', 'Pulsar'] },
            { key: 'type', label: 'Típus', type: 'select', options: ['Vezeték nélküli', 'Vezetékes'] },
        ]
    },
    cable: {
        title: 'Kábel Szűrők',
        filters: [
            { key: 'type', label: 'Típus', type: 'select', options: ['HDMI', 'DisplayPort', 'USB-C', 'USB-A', 'Ethernet', 'Tápkábel'] },
        ]
    },
    headset: {
        title: 'Fejhallgató Szűrők',
        filters: [
            { key: 'brand', label: 'Márka', type: 'select', options: ['HyperX', 'SteelSeries', 'Razer', 'Logitech', 'Corsair', 'JBL'] },
            { key: 'type', label: 'Típus', type: 'select', options: ['Vezeték nélküli', 'Vezetékes', 'USB'] },
        ]
    }
};

const FilterContent = ({ search, setSearch, categories, selectedCategory, setSelectedCategory, selectedSubcategory, setSelectedSubcategory, selectedCondition, setSelectedCondition, priceRange, setPriceRange, hasActiveFilters, clearFilters, selectedBrand, setSelectedBrand, specFilters, setSpecFilters, inStockFilter, setInStockFilter }) => {
    // Determine which category-specific filters to show
    const getActiveFilterConfig = () => {
        if (selectedSubcategory && categoryFilters[selectedSubcategory]) {
            return categoryFilters[selectedSubcategory];
        }
        if (selectedCategory === 'gaming_pc' || selectedCategory === 'office_pc' || selectedCategory === 'workstation') {
            return categoryFilters.pc;
        }
        if (selectedCategory === 'laptop') {
            return categoryFilters.laptop;
        }
        if (selectedCategory === 'mobile_phone') {
            return categoryFilters.mobile_phone;
        }
        if (selectedCategory === 'accessory') {
            if (selectedSubcategory && categoryFilters[selectedSubcategory]) {
                return categoryFilters[selectedSubcategory];
            }
            return categoryFilters.accessory;
        }
        return null;
    };

    const activeFilterConfig = getActiveFilterConfig();
    
    // Handle filter click - toggle filter value
    const handleFilterClick = (filterKey, value) => {
        if (filterKey === 'brand') {
            setSelectedBrand(selectedBrand === value ? '' : value);
        } else {
            setSpecFilters(prev => ({
                ...prev,
                [filterKey]: prev[filterKey] === value ? '' : value
            }));
        }
    };
    
    // Check if a filter option is selected
    const isFilterSelected = (filterKey, value) => {
        if (filterKey === 'brand') {
            return selectedBrand === value;
        }
        return specFilters[filterKey] === value;
    };

    return (
        <div className="space-y-6">
            <div>
                <Label className="text-sm font-medium mb-2 block">Keresés</Label>
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input placeholder="Termék keresése..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="search-input" />
                </div>
            </div>

            {/* Only show PC Rendszerek when NOT viewing components */}
            {selectedCategory !== 'component' && !selectedSubcategory && (
                <Accordion type="single" collapsible defaultValue={['gaming_pc','office_pc','workstation','laptop'].includes(selectedCategory) ? 'pc-categories' : undefined}>
                    <AccordionItem value="pc-categories" className="border-border">
                        <AccordionTrigger className="text-sm font-medium py-2">PC Rendszerek</AccordionTrigger>
                        <AccordionContent>
                            <div className="space-y-2 pt-2">
                                {categories?.pc_categories?.map((cat) => (
                                    <button 
                                        key={cat.id} 
                                        onClick={() => { setSelectedCategory(cat.id); setSelectedSubcategory(''); setSelectedBrand(''); }}
                                        className={cn("w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors",
                                            selectedCategory === cat.id ? "bg-primary text-primary-foreground" : "hover:bg-secondary text-muted-foreground")}
                                        data-testid={`filter-${cat.id}`}>
                                        {getCategoryName(cat.id)}
                                    </button>
                                ))}
                            </div>
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>
            )}

            <Accordion type="single" collapsible defaultValue={selectedCategory === 'component' ? 'components' : undefined}>
                <AccordionItem value="components" className="border-border">
                    <AccordionTrigger className="text-sm font-medium py-2">Alkatrészek</AccordionTrigger>
                    <AccordionContent>
                        <div className="space-y-2 pt-2">
                            {categories?.component_categories?.map((cat) => {
                                const Icon = componentIcons[cat.id] || Cpu;
                                return (
                                    <button 
                                        key={cat.id} 
                                        onClick={() => { setSelectedCategory('component'); setSelectedSubcategory(cat.id); setSelectedBrand(''); }}
                                        className={cn("w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors",
                                            selectedSubcategory === cat.id ? "bg-primary text-primary-foreground" : "hover:bg-secondary text-muted-foreground")}
                                        data-testid={`filter-${cat.id}`}>
                                        <Icon className="w-4 h-4" />
                                        {getCategoryName(cat.id)}
                                    </button>
                                );
                            })}
                        </div>
                    </AccordionContent>
                </AccordionItem>
            </Accordion>

            <Accordion type="single" collapsible defaultValue={selectedCategory === 'accessory' ? 'accessories' : undefined}>
                <AccordionItem value="accessories" className="border-border">
                    <AccordionTrigger className="text-sm font-medium py-2">Tartozékok</AccordionTrigger>
                    <AccordionContent>
                        <div className="space-y-2 pt-2">
                            {[
                                { id: 'monitor', name: 'Monitorok', icon: Monitor },
                                { id: 'keyboard', name: 'Billentyűzetek', icon: Box },
                                { id: 'mouse', name: 'Egerek', icon: Box },
                                { id: 'cable', name: 'Kábelek', icon: Box },
                                { id: 'headset', name: 'Fejhallgatók', icon: Box },
                            ].map((cat) => {
                                const Icon = cat.icon;
                                return (
                                    <button 
                                        key={cat.id} 
                                        onClick={() => { setSelectedCategory('accessory'); setSelectedSubcategory(cat.id); setSelectedBrand(''); }}
                                        className={cn("w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors",
                                            selectedSubcategory === cat.id ? "bg-primary text-primary-foreground" : "hover:bg-secondary text-muted-foreground")}
                                        data-testid={`filter-${cat.id}`}>
                                        <Icon className="w-4 h-4" />
                                        {cat.name}
                                    </button>
                                );
                            })}
                        </div>
                    </AccordionContent>
                </AccordionItem>
            </Accordion>

            {/* Category-specific filters */}
            {activeFilterConfig && (
                <Accordion type="single" collapsible defaultValue={activeFilterConfig ? 'category-filters' : undefined}>
                    <AccordionItem value="category-filters" className="border-border">
                        <AccordionTrigger className="text-sm font-medium py-2">{activeFilterConfig.title}</AccordionTrigger>
                        <AccordionContent>
                            <div className="space-y-4 pt-2">
                                {activeFilterConfig.filters.map((filter) => (
                                    <div key={filter.key}>
                                        <Label className="text-xs text-muted-foreground mb-1 block">{filter.label}</Label>
                                        <div className="flex flex-wrap gap-1">
                                            {filter.options.map((option) => (
                                                <button
                                                    key={option}
                                                    onClick={() => handleFilterClick(filter.key, option)}
                                                    className={cn(
                                                        "px-2 py-1 text-xs rounded-md border transition-colors",
                                                        isFilterSelected(filter.key, option)
                                                            ? "bg-primary text-primary-foreground border-primary"
                                                            : "bg-secondary/50 border-border hover:bg-secondary"
                                                    )}
                                                >
                                                    {option}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>
            )}

            <div>
                <Label className="text-sm font-medium mb-3 block">Elérhetőség</Label>
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <Checkbox id="in-stock" checked={inStockFilter === true} onCheckedChange={(c) => setInStockFilter(c ? true : null)} data-testid="filter-in-stock" />
                        <Label htmlFor="in-stock" className="text-sm cursor-pointer">Készleten</Label>
                    </div>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Raktáron + Beszállítói raktáron</p>
            </div>

            <div>
                <Label className="text-sm font-medium mb-3 block">Állapot</Label>
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <Checkbox id="new" checked={selectedCondition === 'new'} onCheckedChange={(c) => setSelectedCondition(c ? 'new' : '')} data-testid="filter-condition-new" />
                        <Label htmlFor="new" className="text-sm cursor-pointer">Új</Label>
                    </div>
                    <div className="flex items-center gap-2">
                        <Checkbox id="refurbished" checked={selectedCondition === 'refurbished'} onCheckedChange={(c) => setSelectedCondition(c ? 'refurbished' : '')} data-testid="filter-condition-refurbished" />
                        <Label htmlFor="refurbished" className="text-sm cursor-pointer">Felújított</Label>
                    </div>
                </div>
            </div>

            <div>
                <Label className="text-sm font-medium mb-3 block">Ár tartomány</Label>
                <div className="flex items-center gap-2">
                    <Input 
                        type="number" 
                        placeholder="Min" 
                        value={priceRange[0] || ''} 
                        onChange={(e) => setPriceRange([parseInt(e.target.value) || 0, priceRange[1]])}
                        className="text-sm"
                    />
                    <span className="text-muted-foreground">-</span>
                    <Input 
                        type="number" 
                        placeholder="Max" 
                        value={priceRange[1] === 5000000 ? '' : priceRange[1]} 
                        onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value) || 5000000])}
                        className="text-sm"
                    />
                </div>
                <p className="text-xs text-muted-foreground mt-1">Ft</p>
            </div>

            {hasActiveFilters && (
                <Button variant="outline" className="w-full" onClick={clearFilters}>
                    <X className="w-4 h-4 mr-2" />Szűrők törlése
                </Button>
            )}
        </div>
    );
};

export default function ProductsPage({ overrideParams }) {
    usePageMeta('Termékek', 'Böngéssz PC-k, alkatrészek és kiegészítők között. NexusRig PC Webshop.');
    const [searchParams, setSearchParams] = useSearchParams();
    const params = overrideParams || searchParams;
    const { trackSearch, trackCategoryView } = useAnalytics();
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filterOpen, setFilterOpen] = useState(false);
    const [search, setSearch] = useState(params.get('search') || '');
    const [selectedCategory, setSelectedCategory] = useState(params.get('category') || '');
    const [selectedSubcategory, setSelectedSubcategory] = useState(params.get('subcategory') || '');
    const [selectedCondition, setSelectedCondition] = useState(params.get('condition') || '');
    const [selectedBrand, setSelectedBrand] = useState(params.get('brand') || '');
    const [specFilters, setSpecFilters] = useState({});
    const [priceRange, setPriceRange] = useState([0, 5000000]);
    const [sortBy, setSortBy] = useState('default');
    const [inStockFilter, setInStockFilter] = useState(null);

    // Sync state from URL when params change (e.g., from navbar navigation)
    useEffect(() => {
        const urlCategory = params.get('category') || '';
        const urlSubcategory = params.get('subcategory') || '';
        const urlCondition = params.get('condition') || '';
        const urlSearch = params.get('search') || '';
        const urlBrand = params.get('brand') || '';
        
        if (urlCategory !== selectedCategory) setSelectedCategory(urlCategory);
        if (urlSubcategory !== selectedSubcategory) setSelectedSubcategory(urlSubcategory);
        if (urlCondition !== selectedCondition) setSelectedCondition(urlCondition);
        if (urlSearch !== search) setSearch(urlSearch);
        if (urlBrand !== selectedBrand) setSelectedBrand(urlBrand);
        
        if (urlSearch && urlSearch !== search) {
            trackSearch(urlSearch);
        }
        if (urlCategory && urlCategory !== selectedCategory) {
            trackCategoryView(urlSubcategory || urlCategory);
        }
    }, [searchParams, overrideParams]);

    useEffect(() => {
        productsAPI.getCategories().then((res) => setCategories(res.data)).catch(() => {});
    }, []);

    // Filter products client-side based on specFilters (for specs not supported by backend)
    const filterProductsBySpecs = (products) => {
        if (Object.keys(specFilters).filter(k => specFilters[k]).length === 0) {
            return products;
        }
        
        // Chipset to search terms mapping for GPUs
        const chipsetSearchTerms = {
            'NVIDIA': ['nvidia', 'geforce', 'rtx', 'gtx'],
            'AMD': ['amd', 'radeon', 'rx '],
            'Intel': ['intel', 'arc ']
        };
        
        return products.filter(product => {
            for (const [key, value] of Object.entries(specFilters)) {
                if (!value) continue;
                
                // Special handling for GPU chipset filter
                if (key === 'chipset' && selectedSubcategory === 'gpu') {
                    const searchTerms = chipsetSearchTerms[value] || [value.toLowerCase()];
                    const productName = product.name?.toLowerCase() || '';
                    const productBrand = product.brand?.toLowerCase() || '';
                    
                    const chipsetMatch = searchTerms.some(term => 
                        productName.includes(term) || productBrand.includes(term)
                    );
                    
                    if (!chipsetMatch) return false;
                    continue;
                }
                
                // Special handling for manufacturer filter - check brand field
                if (key === 'manufacturer') {
                    const brandMatch = product.brand?.toLowerCase() === value.toLowerCase();
                    if (!brandMatch) return false;
                    continue;
                }
                
                // Check in product name (for PC categories like cpu, gpu, ram filters)
                const nameMatch = product.name?.toLowerCase().includes(value.toLowerCase());
                
                // Check in specs
                const specsMatch = product.specs && Object.values(product.specs).some(
                    specVal => String(specVal).toLowerCase().includes(value.toLowerCase())
                );
                
                // Check in hidden_specs
                const hiddenSpecsMatch = product.hidden_specs && Object.values(product.hidden_specs).some(
                    specVal => String(specVal).toLowerCase().includes(value.toLowerCase())
                );
                
                if (!nameMatch && !specsMatch && !hiddenSpecsMatch) {
                    return false;
                }
            }
            return true;
        });
    };

    useEffect(() => {
        setLoading(true);
        const params = {};
        if (selectedCategory) params.category = selectedCategory;
        if (selectedSubcategory) params.subcategory = selectedSubcategory;
        if (selectedCondition) params.condition = selectedCondition;
        if (search) params.search = search;
        if (selectedBrand) params.brand = selectedBrand;
        if (priceRange[0] > 0) params.min_price = priceRange[0];
        if (priceRange[1] < 5000000) params.max_price = priceRange[1];
        if (inStockFilter === true) params.in_stock = true;
        params.limit = 2000; // Fetch more products to show full catalog
        productsAPI.getAll(params).then((res) => { 
            const filtered = filterProductsBySpecs(res.data);
            setProducts(filtered); 
            setLoading(false); 
        }).catch(() => setLoading(false));
    }, [selectedCategory, selectedSubcategory, selectedCondition, search, selectedBrand, priceRange, specFilters, inStockFilter]);

    const sortedProducts = useMemo(() => {
        const sorted = [...products];
        switch (sortBy) {
            case 'price_asc': return sorted.sort((a, b) => (a.price || 0) - (b.price || 0));
            case 'price_desc': return sorted.sort((a, b) => (b.price || 0) - (a.price || 0));
            case 'name_asc': return sorted.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'hu'));
            case 'name_desc': return sorted.sort((a, b) => (b.name || '').localeCompare(a.name || '', 'hu'));
            case 'newest': return sorted.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
            default: return sorted;
        }
    }, [products, sortBy]);

    useEffect(() => {
        if (overrideParams) return; // Don't rewrite URL for slug-based category pages
        const params = new URLSearchParams();
        if (selectedCategory) params.set('category', selectedCategory);
        if (selectedSubcategory) params.set('subcategory', selectedSubcategory);
        if (selectedCondition) params.set('condition', selectedCondition);
        if (search) params.set('search', search);
        if (selectedBrand) params.set('brand', selectedBrand);
        setSearchParams(params, { replace: true });
    }, [selectedCategory, selectedSubcategory, selectedCondition, search, selectedBrand, setSearchParams, overrideParams]);

    const clearFilters = () => {
        setSelectedCategory(''); setSelectedSubcategory(''); setSelectedCondition(''); setSearch(''); setSelectedBrand(''); setSpecFilters({}); setPriceRange([0, 5000000]); setInStockFilter(null);
    };

    const hasActiveFilters = selectedCategory || selectedSubcategory || selectedCondition || search || selectedBrand || Object.values(specFilters).some(v => v) || priceRange[0] > 0 || priceRange[1] < 5000000 || inStockFilter !== null;

    const filterProps = { search, setSearch, categories, selectedCategory, setSelectedCategory, selectedSubcategory, setSelectedSubcategory, selectedCondition, setSelectedCondition, priceRange, setPriceRange, hasActiveFilters, clearFilters, selectedBrand, setSelectedBrand, specFilters, setSpecFilters, inStockFilter, setInStockFilter };

    const title = selectedCategory ? getCategoryName(selectedCategory) : 'Összes termék';
    const subtitle = selectedSubcategory ? ` - ${getCategoryName(selectedSubcategory)}` : '';

    return (
        <div className="min-h-screen" data-testid="products-page">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="font-heading text-3xl font-bold mb-1">{title}{subtitle}</h1>
                        <p className="text-muted-foreground">{loading ? 'Betöltés...' : `${sortedProducts.length} keresési találat`}</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Select value={sortBy} onValueChange={setSortBy}>
                            <SelectTrigger className="w-[200px] hidden sm:flex" data-testid="sort-dropdown">
                                <ArrowUpDown className="w-4 h-4 mr-2" />
                                <SelectValue placeholder="Rendezés" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="default">Alapértelmezett</SelectItem>
                                <SelectItem value="price_asc">Ár: növekvő</SelectItem>
                                <SelectItem value="price_desc">Ár: csökkenő</SelectItem>
                                <SelectItem value="name_asc">Név: A-Z</SelectItem>
                                <SelectItem value="name_desc">Név: Z-A</SelectItem>
                                <SelectItem value="newest">Legújabb</SelectItem>
                            </SelectContent>
                        </Select>
                        <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
                        <SheetTrigger asChild>
                            <Button variant="outline" className="lg:hidden">
                                <SlidersHorizontal className="w-4 h-4 mr-2" />Szűrők
                                {hasActiveFilters && <span className="ml-2 w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">!</span>}
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="left" className="w-80 overflow-y-auto">
                            <h2 className="font-heading font-semibold text-lg mb-6">Szűrők</h2>
                            <FilterContent {...filterProps} />
                        </SheetContent>
                    </Sheet>
                    </div>
                </div>

                <div className="flex gap-8">
                    <aside className="hidden lg:block w-64 flex-shrink-0">
                        <div className="sticky top-24 glass rounded-xl p-6 max-h-[calc(100vh-8rem)] overflow-y-auto">
                            <h2 className="font-heading font-semibold text-lg mb-6">Szűrők</h2>
                            <FilterContent {...filterProps} />
                        </div>
                    </aside>

                    <main className="flex-1">
                        {loading ? (
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                                {[1,2,3,4,5,6,7,8,9,10].map(i => (
                                    <div key={i} className="aspect-square rounded-lg skeleton" />
                                ))}
                            </div>
                        ) : sortedProducts.length > 0 ? (
                            PC_CATEGORIES.has(selectedCategory) ? (
                                <div className="flex flex-col gap-3" data-testid="pc-list-view">
                                    {sortedProducts.map((product) => {
                                        var specs = product.specs || {};
                                        var imgSrc = (product.images && product.images[0]) ? proxyImg(product.images[0]) : null;
                                        var discount = product.original_price && product.original_price > product.price 
                                            ? Math.round((1 - product.price / product.original_price) * 100) : 0;
                                        return (
                                            <a key={product.id} href={productUrl(product)}
                                                className="group flex flex-col sm:flex-row sm:items-stretch gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl border border-border bg-card hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5 transition-all duration-200 overflow-hidden"
                                                data-testid={'pc-row-' + product.id}>
                                                {/* Image */}
                                                <div className="w-full h-40 sm:w-40 sm:h-40 flex-shrink-0 rounded-lg overflow-hidden bg-secondary">
                                                    {imgSrc ? (
                                                        <img src={imgSrc} alt={product.name} className="w-full h-full object-contain"
                                                            onError={function(e) { e.target.onerror = null; e.target.style.display = 'none'; }} />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                                                            <Monitor className="w-10 h-10 opacity-30" />
                                                        </div>
                                                    )}
                                                </div>
                                                {/* Info */}
                                                <div className="flex-1 flex flex-col justify-between min-w-0 py-1">
                                                    <div>
                                                        <p className="text-xs text-muted-foreground">{product.brand}</p>
                                                        <h3 className="font-heading font-semibold text-base sm:text-lg truncate group-hover:text-primary transition-colors">
                                                            {product.name}
                                                        </h3>
                                                        <div className="flex items-center gap-2 mt-1">
                                                            {discount > 0 && (
                                                                <span className="text-xs line-through text-muted-foreground">
                                                                    {formatPrice(product.original_price)}
                                                                </span>
                                                            )}
                                                            <span className="font-heading font-bold text-lg text-primary">
                                                                {formatPrice(product.price)}
                                                            </span>
                                                            {discount > 0 && (
                                                                <span className="text-xs bg-red-500/10 text-red-500 px-1.5 py-0.5 rounded-full font-medium">
                                                                    -{discount}%
                                                                </span>
                                                            )}
                                                        </div>
                                                        {/* Specs row */}
                                                        {Object.keys(specs).length > 0 && (
                                                            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
                                                                {['cpu', 'gpu', 'ram', 'storage', 'psu'].map(function(key) {
                                                                    if (!specs[key]) return null;
                                                                    var Icon = specIcons[key] || Cpu;
                                                                    return (
                                                                        <div key={key} className="flex items-center gap-1 text-[11px] text-muted-foreground">
                                                                            <Icon className="w-3 h-3 text-primary/60 flex-shrink-0" />
                                                                            <span className="font-medium text-foreground/80">{specLabels[key]}</span>
                                                                            <span className="truncate max-w-[120px] sm:max-w-none">{specs[key]}</span>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center justify-between mt-2">
                                                        <span className={cn("text-xs font-medium", getStockStatus(product).color)}>
                                                            {getStockStatus(product).label}
                                                        </span>
                                                        <span className="text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                                                            Részletek <ChevronRight className="w-3 h-3" />
                                                        </span>
                                                    </div>
                                                </div>
                                            </a>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                                    {sortedProducts.map((product) => (
                                        <ProductCard key={`product-${product.id}`} product={product} compact />
                                    ))}
                                </div>
                            )
                        ) : (
                            <div className="text-center py-16">
                                <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mx-auto mb-4">
                                    <Search className="w-8 h-8 text-muted-foreground" />
                                </div>
                                <h3 className="font-heading font-semibold text-lg mb-2">Nincs találat</h3>
                                <p className="text-muted-foreground mb-4">Próbáld módosítani a szűrőket</p>
                                <Button onClick={clearFilters}>Szűrők törlése</Button>
                            </div>
                        )}
                    </main>
                </div>
            </div>
        </div>
    );
}
